import React, { useState } from 'react';

export function ContactForm({ className = '', isLight = false }) {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
    website_url: '', // Honeypot field
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState(null); // 'success' | 'error' | null

  const validate = () => {
    const errs = {};
    if (!formData.name.trim()) errs.name = 'Venligst indtast dit navn.';
    if (!formData.phone.trim()) errs.phone = 'Venligst indtast dit telefonnummer.';
    if (!formData.email.trim()) {
      errs.email = 'Venligst indtast din e-mailadresse.';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errs.email = 'Indtast venligst en gyldig e-mailadresse.';
    }
    if (!formData.message.trim()) errs.message = 'Venligst indtast en beskrivelse.';
    return errs;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setStatus(null);

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          message: formData.message,
          website_url: formData.website_url, // Mandatory honeypot
          subject: 'Henvendelse fra KELFAE LOFTER B2B Website',
        }),
      });

      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', phone: '', email: '', message: '', website_url: '' });
      } else {
        setStatus('error');
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className={`space-y-4 ${className}`}>
      {status === 'success' && (
        <div className="p-4 bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 rounded-xl text-sm flex items-start gap-3 backdrop-blur-md shadow-lg">
          <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0 text-emerald-400">
            <i className="fa-solid fa-check text-sm" aria-hidden="true" />
          </div>
          <div>
            <p className="font-semibold text-emerald-200">Mange tak for din henvendelse!</p>
            <p className="text-xs text-emerald-300/80 mt-0.5">Vi har modtaget din besked og rådgiver dig inden for kort tid.</p>
          </div>
        </div>
      )}

      {status === 'error' && (
        <div className="p-4 bg-rose-500/15 border border-rose-500/30 text-rose-300 rounded-xl text-sm flex items-start gap-3 backdrop-blur-md shadow-lg">
          <div className="w-8 h-8 rounded-full bg-rose-500/20 flex items-center justify-center shrink-0 text-rose-400">
            <i className="fa-solid fa-circle-exclamation text-sm" aria-hidden="true" />
          </div>
          <div>
            <p className="font-semibold text-rose-200">Der opstod en fejl ved afsendelsen.</p>
            <p className="text-xs text-rose-300/80 mt-0.5">Prøv venligst igen eller kontakt os på (+45) 43 71 25 40 / jlh@kelfae.dk.</p>
          </div>
        </div>
      )}

      {/* Honeypot field - mandatory sr-only absolute */}
      <div className="sr-only absolute" aria-hidden="true">
        <label htmlFor="website_url">Website URL (leave blank)</label>
        <input
          type="text"
          id="website_url"
          name="website_url"
          tabIndex="-1"
          autoComplete="off"
          value={formData.website_url}
          onChange={handleChange}
        />
      </div>

      {/* Split Top Inputs: Navn & Tlf. Nr. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className={`relative ${isLight ? 'bg-white border-neutral-300' : 'bg-neutral-950/40 border-white/10'} border ${errors.name ? 'border-rose-400/80 ring-2 ring-rose-500/20' : 'focus-within:border-[#7FDCA7] focus-within:ring-2 focus-within:ring-[#7FDCA7]/20'} rounded-xl p-3.5 transition-all duration-200 backdrop-blur-md group`}>
          <div className="flex items-center justify-between mb-1">
            <label htmlFor="name" className={`block text-[10px] font-bold uppercase tracking-wider ${isLight ? 'text-neutral-700' : 'text-neutral-300'} font-sans`}>
              Navn <span className="text-[#7FDCA7]">*</span>
            </label>
            <i className="fa-solid fa-user text-xs text-neutral-400 group-focus-within:text-[#7FDCA7] transition-colors" aria-hidden="true" />
          </div>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="f.eks. Lars Hansen"
            className={`w-full bg-transparent ${isLight ? 'text-neutral-900 placeholder-neutral-400' : 'text-white placeholder-neutral-500'} text-sm outline-none font-sans`}
          />
          {errors.name && <p className="text-[11px] text-rose-500 mt-1 font-sans flex items-center gap-1"><i className="fa-solid fa-circle-info text-[9px]"></i>{errors.name}</p>}
        </div>

        <div className={`relative ${isLight ? 'bg-white border-neutral-300' : 'bg-neutral-950/40 border-white/10'} border ${errors.phone ? 'border-rose-400/80 ring-2 ring-rose-500/20' : 'focus-within:border-[#7FDCA7] focus-within:ring-2 focus-within:ring-[#7FDCA7]/20'} rounded-xl p-3.5 transition-all duration-200 backdrop-blur-md group`}>
          <div className="flex items-center justify-between mb-1">
            <label htmlFor="phone" className={`block text-[10px] font-bold uppercase tracking-wider ${isLight ? 'text-neutral-700' : 'text-neutral-300'} font-sans`}>
              Tlf. Nr. <span className="text-[#7FDCA7]">*</span>
            </label>
            <i className="fa-solid fa-phone text-xs text-neutral-400 group-focus-within:text-[#7FDCA7] transition-colors" aria-hidden="true" />
          </div>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="(+45) 43 71 25 40"
            className={`w-full bg-transparent ${isLight ? 'text-neutral-900 placeholder-neutral-400' : 'text-white placeholder-neutral-500'} text-sm outline-none font-sans`}
          />
          {errors.phone && <p className="text-[11px] text-rose-500 mt-1 font-sans flex items-center gap-1"><i className="fa-solid fa-circle-info text-[9px]"></i>{errors.phone}</p>}
        </div>
      </div>

      {/* Full Width Email */}
      <div className={`relative ${isLight ? 'bg-white border-neutral-300' : 'bg-neutral-950/40 border-white/10'} border ${errors.email ? 'border-rose-400/80 ring-2 ring-rose-500/20' : 'focus-within:border-[#7FDCA7] focus-within:ring-2 focus-within:ring-[#7FDCA7]/20'} rounded-xl p-3.5 transition-all duration-200 backdrop-blur-md group`}>
        <div className="flex items-center justify-between mb-1">
          <label htmlFor="email" className={`block text-[10px] font-bold uppercase tracking-wider ${isLight ? 'text-neutral-700' : 'text-neutral-300'} font-sans`}>
            E-mailadresse <span className="text-[#7FDCA7]">*</span>
          </label>
          <i className="fa-solid fa-envelope text-xs text-neutral-400 group-focus-within:text-[#7FDCA7] transition-colors" aria-hidden="true" />
        </div>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="f.eks. lars@virksomhed.dk"
          className={`w-full bg-transparent ${isLight ? 'text-neutral-900 placeholder-neutral-400' : 'text-white placeholder-neutral-500'} text-sm outline-none font-sans`}
        />
        {errors.email && <p className="text-[11px] text-rose-500 mt-1 font-sans flex items-center gap-1"><i className="fa-solid fa-circle-info text-[9px]"></i>{errors.email}</p>}
      </div>

      {/* Message Field */}
      <div className={`relative ${isLight ? 'bg-white border-neutral-300' : 'bg-neutral-950/40 border-white/10'} border ${errors.message ? 'border-rose-400/80 ring-2 ring-rose-500/20' : 'focus-within:border-[#7FDCA7] focus-within:ring-2 focus-within:ring-[#7FDCA7]/20'} rounded-xl p-3.5 transition-all duration-200 backdrop-blur-md group`}>
        <div className="flex items-center justify-between mb-1">
          <label htmlFor="message" className={`block text-[10px] font-bold uppercase tracking-wider ${isLight ? 'text-neutral-700' : 'text-neutral-300'} font-sans`}>
            Projektbeskrivelse / Besked <span className="text-[#7FDCA7]">*</span>
          </label>
          <i className="fa-solid fa-pen-to-square text-xs text-neutral-400 group-focus-within:text-[#7FDCA7] transition-colors" aria-hidden="true" />
        </div>
        <textarea
          id="message"
          name="message"
          rows="4"
          value={formData.message}
          onChange={handleChange}
          placeholder="Beskriv jeres projekt, m² eller spørgsmål til akustikløsninger..."
          className={`w-full bg-transparent ${isLight ? 'text-neutral-900 placeholder-neutral-400' : 'text-white placeholder-neutral-500'} text-sm outline-none resize-none font-sans`}
        />
        {errors.message && <p className="text-[11px] text-rose-500 mt-1 font-sans flex items-center gap-1"><i className="fa-solid fa-circle-info text-[9px]"></i>{errors.message}</p>}
      </div>

      {/* Form Submit Button */}
      <div className="pt-2">
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full sm:w-auto bg-[#7FDCA7] hover:bg-[#69C892] disabled:bg-[#7FDCA7]/50 text-neutral-950 font-sans font-bold text-sm px-8 py-3.5 rounded-xl transition-all shadow-lg hover:shadow-[#7FDCA7]/25 hover:scale-[1.01] active:scale-[0.99] flex items-center justify-center gap-2.5 cursor-pointer group"
        >
          {isSubmitting ? (
            <>
              <i className="fa-solid fa-spinner fa-spin text-sm" aria-hidden="true" />
              <span>Sender din henvendelse...</span>
            </>
          ) : (
            <>
              <span>Send besked</span>
              <i className="fa-solid fa-paper-plane text-xs group-hover:translate-x-1 group-hover:-translate-y-0.5 transition-transform" aria-hidden="true" />
            </>
          )}
        </button>
      </div>
    </form>
  );
}
